import { formatMoney } from '@theme/money-formatting';

/**
 * <atc-dynamic-price> — keeps the price shown on the Add to cart button in sync
 * with the current quantity and variant.
 *
 * The button is server-rendered with the UNIT price (and unit compare-at) stored
 * in data attributes. This element recomputes `unit × quantity` whenever:
 *  - the quantity changes (native +/- fires `quantity-selector:update`; the
 *    bundle selector dispatches the same event; direct typing fires input/change)
 *  - the variant changes (product-form morphs the button → data-unit-price
 *    changes → observedAttributes → re-render on the next frame, after morph).
 *
 * No price data is hardcoded — everything derives from variant.price /
 * compare_at_price (in minor units) + the shop money format.
 */
class AtcDynamicPrice extends HTMLElement {
  static get observedAttributes() {
    return ['data-unit-price', 'data-unit-regular'];
  }

  connectedCallback() {
    this.productForm = this.closest('product-form-component');
    this.scope = this.productForm || document;

    this.onQtyChange = (event) => {
      // For raw input/change, only react to the quantity field.
      if (event.type === 'change' || event.type === 'input') {
        const target = event.target;
        if (!(target instanceof Element) || target.getAttribute('name') !== 'quantity') return;
      }
      this.#scheduleRender();
    };

    this.scope.addEventListener('quantity-selector:update', this.onQtyChange);
    this.scope.addEventListener('change', this.onQtyChange);
    this.scope.addEventListener('input', this.onQtyChange);

    this.#scheduleRender();
  }

  disconnectedCallback() {
    if (this.scope && this.onQtyChange) {
      this.scope.removeEventListener('quantity-selector:update', this.onQtyChange);
      this.scope.removeEventListener('change', this.onQtyChange);
      this.scope.removeEventListener('input', this.onQtyChange);
    }
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  attributeChangedCallback() {
    // Fires while the button is being morphed on variant change. Defer to a
    // frame so the morph (attributes AND children) has fully settled first.
    this.#scheduleRender();
  }

  #scheduleRender() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this.render());
  }

  #quantity() {
    const form = this.productForm || this.closest('form');
    const input = form ? form.querySelector('input[name="quantity"]') : null;
    const value = input ? parseInt(input.value, 10) : 1;
    return value && value > 0 ? value : 1;
  }

  /**
   * Discount % applied to the shown price. The bundle selector stores it on the
   * <product-form-component> (which isn't morphed on variant change, so it
   * survives). 0 when no bundle discount is active.
   */
  #discountPercent() {
    const host = this.productForm;
    const pct = host && host.dataset ? parseInt(host.dataset.discountPercent, 10) : 0;
    return pct && pct > 0 && pct < 100 ? pct : 0;
  }

  /**
   * Exact per-unit price (minor units) set by the bundle selector when the
   * merchant configured a price override (rounded pricing). Wins over the %.
   */
  #unitPriceOverride() {
    const host = this.productForm;
    const cents = host && host.dataset ? parseInt(host.dataset.unitPriceCents, 10) : 0;
    return cents && cents > 0 ? cents : 0;
  }

  /**
   * Prix total exact (unités mineures) posé par le sélecteur de packs.
   * Évite les arrondis d'un prix unitaire non divisible. Prioritaire sur tout.
   */
  #totalPriceOverride() {
    const host = this.productForm;
    const cents = host && host.dataset ? parseInt(host.dataset.totalPriceCents, 10) : 0;
    return cents && cents > 0 ? cents : 0;
  }

  render() {
    const currentEl = this.querySelector('.add-to-cart-price__current');
    if (!currentEl) return;

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const unit = parseInt(this.dataset.unitPrice, 10) || 0;
    const unitRegular = parseInt(this.dataset.unitRegular, 10) || unit;
    const qty = this.#quantity();
    const factor = (100 - this.#discountPercent()) / 100;

    // Discounted price actually shown, and the "before" price to strike through.
    // An exact per-unit override from the bundle selector wins over the %.
    const override = this.#unitPriceOverride();
    const totalOverride = this.#totalPriceOverride();
    const current = totalOverride
      ? totalOverride
      : override
        ? override * qty
        : Math.round(unit * qty * factor);
    const regular = unitRegular * qty;

    // Chaînes déjà formatées par Liquid (correctes pour le marché courant) : prioritaires.
    const host = this.productForm;
    const moneyOverride = host && host.dataset ? host.dataset.totalMoney : '';
    const compareOverride = host && host.dataset ? host.dataset.compareMoney : '';

    currentEl.innerHTML = moneyOverride || formatMoney(current, format, currency);

    const compareEl = this.querySelector('.add-to-cart-price__compare');
    if (compareEl && moneyOverride) {
      if (compareOverride) {
        compareEl.innerHTML = compareOverride;
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    } else if (compareEl) {
      if (regular > current) {
        compareEl.innerHTML = formatMoney(regular, format, currency);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }
}

if (!customElements.get('atc-dynamic-price')) {
  customElements.define('atc-dynamic-price', AtcDynamicPrice);
}
